import { CssVarsThemeOptions } from '@mui/material';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import themeV2 from '../dap';
import { DialogClass, TabsClass, ToggleButtonGroupClass } from '../dap/classes';
import {
  blockTab,
  dataGridBoldClass,
  dataGridCellHoverClass,
  dataGridCellNoFocusOutlineClass,
  dataGridColorRowClass,
  dataGridHorizontalPaddingXSClass,
  dataGridNoHorizontalPaddingClass,
  dataGridRowHoverClass,
  dataGridSelectedRowClass,
  dataGridWhiteRowClass,
  dataGridWithCellBordersClass,
} from './styleConstants';
import { ExpandMoreRounded } from '@mui/icons-material';

// Style callbacks read theme.vars.palette so values follow the active colour scheme;
// theme.palette would always resolve to the light scheme.
export const componentsStyles: CssVarsThemeOptions['components'] = {
  ...themeV2.components,
  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => ({
        padding: 0,
        borderRadius: theme.spacing(1),
        boxShadow: theme.shadows[4],
        position: 'relative',
        overflowY: 'unset',
        gap: theme.spacing(2),
        '&.padding-0': {
          padding: 0,
        },
        '&.gap-0': {
          gap: 0,
        },
      }),
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: ({ theme: { typography, spacing } }) => ({
        ...typography.h4,
        padding: spacing(3),
        paddingBottom: 0,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
      }),
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: () => ({ [`&.padding-0`]: { padding: 0 } }),
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: ({ theme: { spacing } }) => ({ padding: spacing(3), paddingTop: 0 }),
    },
  },
  MuiDataGrid: {
    styleOverrides: {
      root: ({
        theme: {
          vars: { palette },
          spacing,
          typography,
        },
      }) => ({
        borderRadius: 0,
        border: 0,
        backgroundColor: palette.background.paper,

        '.MuiDataGrid-columnHeaderTitle': {
          ...typography.body1,
          fontWeight: 600,
          whiteSpace: 'normal',
        },
        '.MuiDataGrid-columnHeader': {
          backgroundColor: palette.background.paper,
          '&:focus': {
            outline: 'transparent',
          },
        },

        // Scrollbar
        '.MuiDataGrid-scrollbarFiller': {
          backgroundColor: `${palette.background.paper} !important`,
        },

        // Containers
        '--DataGrid-containerBackground': palette.background.paper,

        '.MuiDataGrid-topContainer': {
          backgroundColor: palette.background.paper,
          paddingLeft: spacing(2),
        },

        [`&.${dataGridNoHorizontalPaddingClass}`]: {
          '& .MuiDataGrid-topContainer, & .MuiDataGrid-row': {
            paddingLeft: 0,
          },
        },
        [`&.${dataGridHorizontalPaddingXSClass}`]: {
          '& .MuiDataGrid-topContainer, & .MuiDataGrid-row': {
            paddingLeft: spacing(1),
          },
        },

        '.MuiDataGrid-footerContainer': {
          marginTop: 4,
          marginBottom: 2,
          justifyContent: 'center',
        },

        '& .MuiDataGrid-row': {
          ':last-of-type': {
            marginBottom: '2px',
            borderBottom: `1px solid ${palette.divider}`,
          },
        },
        [`&.${dataGridWithCellBordersClass}`]: {
          '.MuiDataGrid-topContainer': {
            backgroundColor: palette.background.paper,
            paddingLeft: '0 !important',
            '& .MuiDataGrid-columnHeader': {
              ':nth-of-type(2)': { paddingLeft: spacing(3) },
            },
          },
          '& .MuiDataGrid-row': {
            paddingLeft: 0,
            border: 'none',
          },
          '& .MuiDataGrid-cell': {
            border: `1px solid ${palette.divider}`,
            flexGrow: 1,
            borderLeft: 'none',
            borderTop: 'none',
            ':last-of-type': { borderRight: 0 },
            ':nth-of-type(2)': { paddingLeft: spacing(3) },
          },
        },

        // Cell
        '.MuiDataGrid-cell': {
          ...typography.body2,
          display: 'flex',
          alignItems: 'center',
          alignContent: 'center',
          color: palette.action.active,
          fontWeight: 400,
          whiteSpace: 'normal !important',

          [`&.${dataGridNoHorizontalPaddingClass}`]: {
            paddingLeft: 0,
            paddingRight: 0,
          },
          [`&.${dataGridBoldClass}`]: {
            overflow: 'visible',
            fontWeight: 500,
            color: palette.text.primary,
          },
          [`&.${dataGridCellNoFocusOutlineClass}:focus`]: {
            outline: 'none',
          },
          ':focus-within, :focus': {
            outline: 'transparent',
          },
        },

        '.MuiDataGrid-cell--pinnedRight, .MuiDataGrid-filler--pinnedRight, .MuiDataGrid-filler': {
          backgroundColor: `${palette.background.paper} !important`,
        },
        '.MuiDataGrid-cell--pinnedLeft, .MuiDataGrid-filler--pinnedLeft': {
          backgroundColor: `${palette.background.paper} !important`,
        },
      }),

      row: ({
        theme: {
          vars: { palette },
          spacing,
          shadows,
        },
      }) => ({
        paddingLeft: spacing(2),

        [`&.${dataGridColorRowClass}`]: {
          backgroundColor: `${palette.grey.A100} !important`,
        },
        [`&.${dataGridWhiteRowClass}`]: {
          backgroundColor: `${palette.background.paper} !important`,
        },

        [`&.${dataGridRowHoverClass}`]: {
          '&:hover': {
            background: `${palette.nav?.activeDark} !important`,
            boxShadow: shadows[2],
            zIndex: 2,
          },
        },
        [`&.${dataGridSelectedRowClass}`]: {
          backgroundColor: `${palette.nav?.activeDark} !important`,
          boxShadow: shadows[2],
          zIndex: 2,
        },
      }),

      cell: () => ({
        [`&.${dataGridCellHoverClass}`]: {
          '&:hover': {
            cursor: 'pointer',
          },
        },
      }),
    },
  },
  MuiButtonBase: {
    styleOverrides: {
      root: {
        fontFamily: 'inherit',
      },
    },
    defaultProps: {
      disableRipple: true,
      disableTouchRipple: true,
    },
  },
  MuiRadio: {
    styleOverrides: {
      root: ({ theme }) => ({ color: theme.vars.palette.text.secondary }),
    },
  },
  MuiButton: {
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        borderWidth: ownerState.size === 'large' ? 2 : 1.5,
        borderStyle: 'solid',
        borderRadius: Number(theme.shape.borderRadius) * 2,
        textTransform: 'none',
        strokeWidth: 2,
        fontWeight: 500,

        '&:hover': {
          borderWidth: ownerState.size === 'large' ? 2 : 1.5,
          borderStyle: 'solid',
          boxShadow: theme.shadows[1],
          transition: 'none', // Remove for smoother border transition
          borderColor: theme.vars.palette.grey[900],
          color: theme.vars.palette.common.white,
          background: theme.vars.palette.grey[900],
          ...theme.applyStyles('dark', { color: theme.vars.palette.background.default }),
        },
        '&:focus-visible': {
          boxShadow: theme.shadows[1],
          outline: `1px solid ${theme.vars.palette.grey[900]}`,
          outlineOffset: 3,
        },
        '&:disabled': {
          borderWidth: ownerState.size === 'large' ? 2 : 1.5,
          borderStyle: 'solid',
          background: theme.vars.palette.grey.A100,
          color: theme.vars.palette.text.disabled,
          borderColor: theme.vars.palette.grey.A100,
        },
      }),
      sizeSmall: ({ theme }) => ({
        ...theme.typography.body2,
        fontWeight: 500,
        padding: '9px 16px',
      }),
      sizeLarge: ({ theme }) => ({
        ...theme.typography.body1,
        fontWeight: 500,
        padding: '10px 38px',
      }),
      containedPrimary: ({ theme }) => ({
        color: theme.vars.palette.common.white,
        background: theme.vars.palette.primary.main,
        transition: 'none', // Remove for smoother border transition
      }),
      containedSecondary: ({ theme }) => ({
        color: theme.vars.palette.text.primary,
        background: theme.vars.palette.gradient.secondary,
        borderColor: 'textSecondary',
        transition: 'none', // Remove for smoother border transition
        ...theme.applyStyles('dark', { color: theme.vars.palette.background.default }),
      }),
      containedWarning: ({ theme }) => ({
        color: theme.vars.palette.text.primary,
        background: theme.vars.palette.warning.main,
        borderColor: theme.vars.palette.warning.main,
        ...theme.applyStyles('dark', { color: theme.vars.palette.background.default }),
      }),
      contained: {
        border: 'none',
        '&.MuiButton-sizeSmall': {
          padding: '10px 20px',
          ':hover, :disabled': {
            border: 0,
            padding: '10px 20px',
          },
        },
        '&.MuiButton-sizeLarge': {
          padding: '12px 40px',
          ':hover, :disabled': {
            border: 0,
            padding: '12px 40px',
          },
        },
        '&.MuiButton-colorError': {
          color: 'white',
        },
      },
      outlined: {
        '&.MuiButton-sizeSmall': {
          padding: '8.5px 18.5px',
          ':hover, :disabled': {
            padding: '8.5px 18.5px',
          },
        },
      },
      outlinedPrimary: ({ theme }) => ({
        color: theme.vars.palette.primary.main,
        background: `linear-gradient(${theme.vars.palette.background.paper}, ${theme.vars.palette.background.paper}) padding-box, ${theme.vars.palette.primary.main} border-box`,
        transition: 'none', // Remove for smoother border transition,
        ...theme.applyStyles('dark', {
          color: theme.vars.palette.primary.light,
          background: `linear-gradient(${theme.vars.palette.background.paper}, ${theme.vars.palette.background.paper}) padding-box, ${theme.vars.palette.primary.light} border-box`,
        }),
      }),
      outlinedSecondary: ({ theme }) => ({
        color: theme.vars.palette.text.secondary,
        background: `linear-gradient(${theme.vars.palette.background.paper}, ${theme.vars.palette.background.paper}) padding-box, ${theme.vars.palette.gradient.secondary} border-box`,
        transition: 'none', // Remove for smoother border transition
      }),
      outlinedWarning: ({ theme }) => ({
        color: theme.vars.palette.text.secondary,
        background: theme.vars.palette.background.paper,
        borderColor: theme.vars.palette.warning.main,
      }),
      text: () => ({
        borderColor: 'transparent',
        transition: 'none', // Remove for smoother border transition
      }),
      textPrimary: ({ theme }) =>
        theme.applyStyles('dark', { color: theme.vars.palette.primary.light }),
    },
    variants: [
      {
        props: { color: 'grey', variant: 'outlined' },
        style: ({ theme }) => ({
          color: theme.vars.palette.text.secondary,
          background: theme.vars.palette.background.paper,
          borderColor: theme.vars.palette.grey.A100,
        }),
      },
      {
        props: { color: 'grey', variant: 'contained' },
        style: ({ theme }) => ({
          color: theme.vars.palette.text.secondary,
          background: theme.vars.palette.background.paper,
          borderColor: theme.vars.palette.background.paper,
        }),
      },
      {
        props: { variant: 'liquid' as any },
        style: ({ theme }) => {
          const transitionType = `cubic-bezier(0.2, 0.9, 0.3, 1.5)`;
          const transitionDuration = `0.3s`;
          return {
            color: theme.vars.palette.primary.main,
            background: 'rgba(255, 255, 255, 0.05)',
            borderColor: 'transparent',
            borderRadius: `calc(${Number(theme.shape.borderRadius) * 2}px)`,
            boxShadow: `inset 2px 2px 1px 0 rgba(255, 255, 255, 0.3), inset -2px -2px 2px 1px rgba(255, 255, 255, 0.3), 0 4px 8px 0 rgba(${theme.vars.palette.primary.mainChannel} / 0.2), 0 6px 20px 0 rgba(${theme.vars.palette.primary.mainChannel} / 0.2)`,
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            transition: `transform ${transitionDuration} ${transitionType}, border-radius ${transitionDuration} ${transitionType}, box-shadow ${transitionDuration} ${transitionType}, background ${transitionDuration} ${transitionType}`,
            textTransform: 'none',
            border: 'none',

            '&.MuiButton-sizeSmall': {
              fontSize: theme.typography.body2.fontSize,
              padding: '10px 20px',
            },
            '&.MuiButton-sizeMedium': {
              fontSize: theme.typography.body1.fontSize,
              padding: '12px 28px',
            },
            '&.MuiButton-sizeLarge': {
              fontSize: theme.typography.h6.fontSize,
              padding: '14px 40px',
            },

            '&:hover,&:focus-visible': {
              color: theme.vars.palette.primary.main,
              transform: 'scale(1.05)',
              background: 'rgba(255, 255, 255, 0.08)',
              border: 'inherit',
              boxShadow: `inset 2px 2px 1px 0 rgba(255, 255, 255, 0.35), inset -2px -2px 2px 1px rgba(255, 255, 255, 0.35), 0 6px 14px 0 rgba(${theme.vars.palette.primary.mainChannel} / 0.25), 0 10px 28px 0 rgba(${theme.vars.palette.primary.mainChannel} / 0.25)`,
              outline: 'none',
              ...theme.applyStyles('dark', { color: theme.vars.palette.primary.light }),
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
            '&.Mui-disabled': {
              color: theme.vars.palette.text.disabled,
              background: 'rgba(255,255,255,0.04)',
              boxShadow: 'none',
            },
            ...theme.applyStyles('dark', { color: theme.vars.palette.primary.light }),
          };
        },
      },
    ],
  },

  MuiToggleButton: {
    defaultProps: {
      disableFocusRipple: true,
      disableRipple: true,
    },
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        borderWidth: ownerState.size === 'large' ? 2 : 1,
        borderStyle: 'solid',
        borderRadius: Number(theme.shape.borderRadius) * 2,
        textTransform: 'none',
        background: theme.vars.palette.background.paper,
        '&.Mui-selected': {
          borderColor: theme.vars.palette.primary.main,
          background: theme.vars.palette.background.paper,
          ...theme.applyStyles('dark', { borderColor: theme.vars.palette.primary.light }),
        },
        '&:hover, &.Mui-selected:hover': {
          borderWidth: ownerState.size === 'large' ? 2 : 1,
          borderStyle: 'solid',
          boxShadow: theme.shadows[1],
          transition: 'none', // Remove for smoother border transition
          borderColor: theme.vars.palette.grey[900],
          color: theme.vars.palette.common.white,
          background: theme.vars.palette.grey[900],
          ...theme.applyStyles('dark', { color: theme.vars.palette.background.default }),
        },
        '&:focus-visible': {
          boxShadow: theme.shadows[1],
        },
        '&:disabled': {
          borderWidth: ownerState.size === 'large' ? 2 : 1,
          borderStyle: 'solid',
          background: theme.vars.palette.grey.A100,
          color: theme.vars.palette.text.disabled,
          borderColor: theme.vars.palette.grey.A100,
        },
      }),
      sizeSmall: ({ theme }) => ({
        ...theme.typography.body2,
        fontWeight: 500,
        padding: '7.5px',
      }),
      sizeLarge: ({ theme }) => ({
        ...theme.typography.body1,
        fontWeight: 500,
        padding: '10px',
      }),

      selected: ({ theme }) => ({
        borderColor: theme.vars.palette.primary.main,
        background: theme.vars.palette.background.paper,
        ...theme.applyStyles('dark', { borderColor: theme.vars.palette.primary.light }),
      }),
    },
    // css state classes didnt work for primary, secondary, standard
    variants: [
      {
        props: { color: 'primary' },
        style: ({ theme }) => ({
          color: theme.vars.palette.primary.main,
          '&.Mui-selected': {
            borderColor: theme.vars.palette.primary.main,
            ...theme.applyStyles('dark', { borderColor: theme.vars.palette.primary.light }),
          },
          ...theme.applyStyles('dark', { color: theme.vars.palette.primary.light }),
        }),
      },
      {
        props: { color: 'secondary' },
        style: ({
          theme: {
            vars: { palette },
          },
        }) => ({
          color: palette.secondary.main,
          '&.Mui-selected': {
            borderColor: palette.secondary.main,
          },
        }),
      },
      {
        props: { color: 'warning' },
        style: ({
          theme: {
            vars: { palette },
          },
        }) => ({
          color: palette.warning.main,
          '&.Mui-selected': {
            borderColor: palette.warning.main,
          },
        }),
      },
      {
        props: { color: 'success' },
        style: ({
          theme: {
            vars: { palette },
          },
        }) => ({
          color: palette.success.main,
          '&.Mui-selected': {
            borderColor: palette.success.main,
          },
        }),
      },
    ],
  },
  MuiPaginationItem: {
    styleOverrides: {
      root: ({
        theme: {
          vars: { palette },
          typography,
          shape,
          spacing,
        },
      }) => ({
        color: palette.text.primary,
        ...typography.body2,
        fontWeight: 600,
        borderRadius: shape.borderRadius,
        '&.MuiButtonBase-root': {
          '& > :first-of-type': {
            outline: `2px solid ${palette.divider}`,
            borderRadius: Number(shape.borderRadius) * 5,
            margin: spacing(1.5),
          },
        },
      }),
      page: ({
        theme: {
          vars: { palette },
        },
      }) => ({
        '&.Mui-selected': {
          background: palette.primary.main,
          color: palette.common.white,
        },
      }),
    },
  },

  MuiInputLabel: {
    styleOverrides: {
      root: () => ({
        fontWeight: `${400} !important`,
      }),
      shrink: ({ ownerState }) => ({
        ...(ownerState.shrink && {
          fontSize: '16px !important',
        }),
      }),
    },
  },

  MuiSwitch: {
    styleOverrides: {
      root: ({
        theme: {
          vars: { palette },
          shape,
          transitions,
        },
      }) => ({
        width: 45,
        height: 25,
        padding: 0,

        '& .MuiSwitch-switchBase': {
          padding: 0,
          margin: 2.5,
          transitionDuration: '300ms',
          '&.Mui-checked': {
            transform: 'translateX(20px)',
            '& + .MuiSwitch-track': {
              backgroundColor: palette.primary.main,
              opacity: 1,
              border: 0,
            },
            '&.Mui-disabled + .MuiSwitch-track': {
              opacity: 0.5,
            },
          },
          '&.Mui-disabled .MuiSwitch-thumb': {
            color: palette.grey[100],
          },
          '&.Mui-disabled + .MuiSwitch-track': {
            opacity: 0.7,
          },
        },
        '& .MuiSwitch-thumb': {
          boxSizing: 'border-box',
          color: palette.common.white,
          width: 20,
          height: 20,
        },
        '& .MuiSwitch-track': {
          borderRadius: Number(shape.borderRadius) * 4,
          backgroundColor: palette.grey[200],
          opacity: 1,
          transition: transitions.create(['background-color'], {
            duration: 500,
          }),
        },
      }),
    },
  },
  MuiMenu: {
    defaultProps: {
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'right',
      },
    },
    styleOverrides: {
      root: ({
        theme: {
          vars: { palette },
        },
      }) => ({
        '& .MuiPaper-root': { padding: 0 },
        '& .MuiMenu-list': {
          padding: 0,
          '& .MuiListSubheader-root': {
            paddingTop: 0,
            lineHeight: 3,
            background: palette.grey.A100,
          },
        },
      }),
    },
  },

  MuiMenuItem: {
    defaultProps: {},
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        gap: theme.spacing(3),
        justifyContent: 'space-between',
        padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
        borderTop: `1px solid ${theme.vars.palette.divider}`,

        '&:last-of-type': {
          borderBottom: `1px solid ${theme.vars.palette.divider}`,
        },

        ':hover': {
          backgroundColor: theme.vars.palette.divider,
        },
        ':focus': {
          backgroundColor: theme.vars.palette.divider,
        },
        ':mouseOver': {
          backgroundColor: theme.vars.palette.divider,
        },
        ...(ownerState.color === 'warning' && {
          backgroundColor: theme.vars.palette.warning.main,
          ':hover': {
            backgroundColor: theme.vars.palette.warning.dark,
          },
          ':focus': {
            backgroundColor: theme.vars.palette.warning.dark,
          },
        }),
        '& .MuiSvgIcon-root': {
          color: theme.vars.palette.primary.main,
          ...theme.applyStyles('dark', { color: theme.vars.palette.primary.light }),
        },
      }),
    },
  },
  // Midnight inverts the grey ramp: grey[900] is near-white in dark, so text on it must be dark.
  MuiTooltip: {
    styleOverrides: {
      ...themeV2.components?.MuiTooltip?.styleOverrides,
      tooltip: [
        themeV2.components?.MuiTooltip?.styleOverrides?.tooltip,
        ({ theme }) => theme.applyStyles('dark', { color: theme.vars.palette.background.default }),
      ],
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: [
        themeV2.components?.MuiIconButton?.styleOverrides?.root,
        ({ theme }) =>
          theme.applyStyles('dark', {
            ':hover': { color: theme.vars.palette.primary.light },
            [`&.${DialogClass.closeButton}:hover`]: {
              color: theme.vars.palette.background.default,
            },
          }),
      ],
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: [
        themeV2.components?.MuiFormLabel?.styleOverrides?.root,
        ({ theme }) =>
          theme.applyStyles('dark', {
            '&.Mui-focused': { color: theme.vars.palette.primary.light },
            '&.Mui-disabled': { color: theme.vars.palette.text.disabled },
          }),
      ],
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: [
        themeV2.components?.MuiFormHelperText?.styleOverrides?.root,
        ({ theme }) =>
          theme.applyStyles('dark', {
            '&.Mui-disabled': {
              color: theme.vars.palette.text.disabled,
              ' svg': { color: theme.vars.palette.text.disabled },
            },
          }),
      ],
    },
  },
  // react-toastify keeps its `theme--light` classes (light stays as is); its custom properties
  // are repointed to the palette in dark.
  MuiCssBaseline: {
    styleOverrides: (theme) => ({
      '.Toastify__toast-container': theme.applyStyles('dark', {
        '--toastify-color-light': theme.vars.palette.background.paper,
        '--toastify-text-color-light': theme.vars.palette.text.primary,
      }),
      '.Toastify__toast-container .Toastify__close-button--light': theme.applyStyles('dark', {
        color: theme.vars.palette.text.primary,
      }),
    }),
  },
  MuiFormControl: {
    styleOverrides: {
      root: ({
        ownerState,
        theme: {
          typography,
          vars: { palette },
        },
      }) => ({
        '& .MuiFormLabel-root': {
          ...(ownerState.size === 'medium' ? typography.body1 : typography.body2),
          fontWeight: 500,
        },
        '& .MuiInputBase-input': {
          height: '20px',
        },

        '& .MuiOutlinedInput-root': {
          '&.Mui-focused': {
            background: palette.background.paper,
          },
        },
        '& .MuiOutlinedInput-input': {
          '&:-webkit-autofill': {
            WebkitBoxShadow: `0 0 0 30px ${palette.background.paper} inset !important`,
          },
        },
      }),
    },
  },

  MuiOutlinedInput: {
    ...themeV2.components?.MuiOutlinedInput,
    variants: [
      {
        props: { size: 'xs' as any },
        style: () => ({
          minWidth: 68,
          height: 32,
          '& input': {
            paddingTop: 0,
            paddingBottom: 0,
          },
        }),
      },
    ],
  },

  MuiPickersOutlinedInput: {
    styleOverrides: {
      notchedOutline: ({
        ownerState,
        theme: {
          vars: { palette },
          transitions,
        },
      }) => ({
        borderWidth: `${ownerState.inputSize === 'medium' ? 2 : 1}px `,
        borderColor: palette.grey.A100,
        transition: transitions.create(['border-color']),
      }),

      sectionsContainer: ({ ownerState }) => ({
        minHeight: ownerState.inputSize === 'medium' ? '20px' : '23px',
      }),

      root: ({ ownerState, theme }) => ({
        borderRadius:
          ownerState.inputSize === 'medium'
            ? Number(theme.shape.borderRadius) * 3
            : Number(theme.shape.borderRadius) * 2,
        background: theme.vars.palette.background.paper,
        ...(ownerState.inputSize === 'medium' ? theme.typography.body1 : theme.typography.body2),
        fontWeight: 500,

        [`&:hover .MuiPickersOutlinedInput-notchedOutline`]: {
          borderColor: theme.vars.palette.primary.main,
          ...theme.applyStyles('dark', { borderColor: theme.vars.palette.primary.light }),
        },

        [`&.Mui-focused`]: {
          background: theme.vars.palette.primary.A100,

          '.MuiPickersOutlinedInput-notchedOutline': {
            borderColor: theme.vars.palette.primary.main,
            borderWidth: `${ownerState.inputSize === 'medium' ? 2 : 1}px `,
            ...theme.applyStyles('dark', { borderColor: theme.vars.palette.primary.light }),
          },
        },

        [`&.Mui-error`]: {
          color: theme.vars.palette.warning[900],
          background: theme.vars.palette.warning[50],

          ' svg': {
            color: theme.vars.palette.warning[900],
          },

          '.MuiPickersOutlinedInput-notchedOutline': {
            borderColor: theme.vars.palette.warning[900],
          },
        },

        [`&.Mui-disabled`]: {
          background: `color-mix(in srgb, ${theme.vars.palette.grey.A100} 20%, transparent)`,

          '.MuiPickersOutlinedInput-notchedOutline': {
            color: theme.vars.palette.text.disabled,
            borderColor: theme.vars.palette.grey.A100,
          },

          ' svg': {
            color: theme.vars.palette.grey[200],
          },
        },
      }),
    },
  },
  MuiPopover: {
    styleOverrides: {
      paper: ({ theme: { spacing } }) => ({
        '& .MuiListSubheader-root': {
          paddingTop: spacing(1),
        },
      }),
    },
  },

  MuiSelect: {
    defaultProps: {
      IconComponent: ExpandMoreRounded,
    },
    styleOverrides: {
      root: () => ({
        '.MuiOutlinedInput-input': {
          padding: '16px 14px', // compensate for 2px border
        },

        '.MuiInputBase-inputSizeSmall': {
          padding: '8.5px 14px', // compensate for 14px font size
          minHeight: '23px',
        },
      }),
    },
  },

  MuiTabs: {
    styleOverrides: {
      root: ({ theme }) => ({
        marginRight: theme.spacing(1),
        '& .MuiTabs-indicator': {
          display: 'none',
        },

        [`&.${TabsClass.underlined}`]: {
          width: '100%',
          minHeight: 0,
          marginRight: 0,
          borderBottom: `1px solid ${theme.vars.palette.grey.A100}`,

          '& .MuiTab-root': {
            minHeight: '10px',
            paddingTop: 0,
            paddingBottom: 12,
            borderRadius: 0,
          },

          '& .MuiTabs-flexContainer': {
            justifyContent: 'start',
            gap: theme.spacing(3),
          },

          '& .MuiTabs-indicator': {
            display: 'block',
            background: theme.vars.palette.primary.main,
            height: '4px',
            borderRadius: Number(theme.shape.borderRadius) * 5,
            ...theme.applyStyles('dark', { background: theme.vars.palette.primary.light }),
          },

          '& .MuiTabScrollButton-horizontal': {
            height: 20,
            width: 20,
          },
        },
      }),
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        border: `1px solid ${theme.vars.palette.grey.A100}`,
        borderTopLeftRadius: Number(theme.shape.borderRadius) * 2,
        borderTopRightRadius: Number(theme.shape.borderRadius) * 2,
        textTransform: 'none',
        fontSize: theme.typography.body1.fontSize,
        color: theme.vars.palette.text.secondary,
        padding: theme.spacing(1, 6),
        minHeight: theme.spacing(6),

        [`&.tab--small`]: { padding: theme.spacing(1, 2) },

        [`:not(.${TabsClass.underlined}):not(.${blockTab})`]: {
          backgroundColor: theme.vars.palette.grey[50],
        },
        [`&:hover:is(.${blockTab})`]: {
          backgroundColor: theme.vars.palette.grey.A100,
        },

        [`&.Mui-selected:not(.${TabsClass.underlined}):not(.${blockTab})`]: {
          boxShadow: '6px 8px 8px rgba(15, 15, 15, 0.2)',
          backgroundColor: theme.vars.palette.background.paper,
          color: theme.vars.palette.text.secondary,
          borderBottom: 0,
          fontWeight: 600,
        },
        [`&.Mui-disabled:not(.${blockTab})`]: {
          borderRadius: 0,
          border: 0,
        },

        [`&.${TabsClass.underlined}`]: {
          border: 0,
          padding: 0,
          spacing: theme.spacing(4),

          '&.Mui-selected': {
            color: theme.vars.palette.text.primary,
            fontWeight: 700,
          },
        },
        [`&.${blockTab}`]: {
          padding: theme.spacing(2),
          borderRadius: 0,
          border: 0,

          '&.Mui-selected': {
            backgroundColor: '#E4E4E4',
            color: theme.vars.palette.text.primary,
            ...theme.applyStyles('dark', { backgroundColor: theme.vars.palette.grey[100] }),
          },
        },
      }),
    },
  },
  MuiAccordion: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&::before': { height: 0 },
        '&.active': {
          '> .MuiAccordionSummary-root': {
            color: theme.vars.palette.primary.main,
            ...theme.applyStyles('dark', { color: theme.vars.palette.primary.light }),
          },
        },
      }),
    },
    defaultProps: {
      disableGutters: true,
      TransitionProps: { unmountOnExit: true, mountOnEnter: true },
      elevation: 0,
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(0.5),
        backgroundColor: theme.vars.palette.primary.A200,
        borderRadius: Number(theme.shape.borderRadius) * 2,
        gap: theme.spacing(0.5),
        [`&.${ToggleButtonGroupClass.outline} .MuiToggleButtonGroup-grouped`]: {
          '&.Mui-selected, &.Mui-selected:hover, &:hover, &:focus': {
            color: 'inherit',
            background: 'transparent',
            boxShadow: 'none',
            outline: `3px solid ${theme.vars.palette.primary.main}`,
            outlineOffset: '-3px',
            ...theme.applyStyles('dark', {
              outline: `3px solid ${theme.vars.palette.primary.light}`,
            }),
          },
          '&:hover, &.Mui-selected:hover': {
            outlineColor: theme.vars.palette.grey[900],
          },
          '&.Mui-selected.MuiToggleButton-primary, &.Mui-selected.MuiToggleButton-standard': {
            color: 'inherit',
            background: 'transparent',
          },
        },
      }),
      grouped: ({ theme }) => ({
        color: theme.vars.palette.text.secondary,
        background: 'transparent',
        border: 'none !important',
        fontWeight: 600,
        '&.MuiToggleButtonGroup-grouped': {
          borderRadius: theme.shape.borderRadius,
          '&:hover,:focus': {
            boxShadow: theme.shadows[1],
            color: theme.vars.palette.common.white,
            background: theme.vars.palette.grey[900],
            ...theme.applyStyles('dark', { color: theme.vars.palette.background.default }),
          },
        },
        '&.Mui-disabled': {
          color: theme.vars.palette.grey[200],
        },
        '&.Mui-selected': {
          '&.MuiToggleButton-primary, &.MuiToggleButton-standard': {
            color: theme.vars.palette.common.white,
            background: theme.vars.palette.primary.main,
          },
          '&:hover': {
            boxShadow: theme.shadows[1],
            color: theme.vars.palette.common.white,
            background: theme.vars.palette.grey[900],
            ...theme.applyStyles('dark', { color: theme.vars.palette.background.default }),
          },
        },
        '&.MuiToggleButton-sizeSmall': {
          padding: `${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
          whiteSpace: 'nowrap',
        },
        '&.MuiToggleButton-sizeMedium': {
          padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
        },
      }),
      middleButton: ({
        theme: {
          vars: { palette },
          spacing,
        },
      }) => ({
        '&::after': {
          content: '""',
          display: 'block',
          width: '0.5px',
          backgroundColor: palette.grey[100],
          position: 'absolute',
          height: '24px',
          right: spacing(-0.5),
          top: '50%',
          transform: 'translateX(-50%) translateY(-50%)',
        },
      }),
      firstButton: ({
        theme: {
          vars: { palette },
          spacing,
        },
      }) => ({
        '&::after': {
          content: '""',
          display: 'block',
          width: '0.5px',
          backgroundColor: palette.grey[100],
          position: 'absolute',
          height: '24px',
          right: spacing(-0.5),
          top: '50%',
          transform: 'translateX(-50%) translateY(-50%)',
        },
      }),
    },
  },
  MuiStepper: {
    styleOverrides: {
      horizontal: ({ theme }) => ({
        justifyContent: 'space-between',
        '.MuiStep-root': {
          padding: theme.spacing(0, 2.25),
          flex: 'none',

          ':first-of-type': {
            paddingLeft: 0,
          },

          ':last-of-type': {
            paddingRight: 0,
          },
        },

        '.MuiStepConnector-root': {
          position: 'inherit',
          flex: 1,
          alignSelf: 'center',
          '&.Mui-active, &.Mui-completed': {
            '.MuiStepConnector-line': {
              borderColor: theme.vars.palette.primary.main,
              ...theme.applyStyles('dark', { borderColor: theme.vars.palette.primary.light }),
            },
          },
          '.MuiStepConnector-line': {
            borderTopWidth: '2px',
            borderColor: theme.vars.palette.grey[400],
            minWidth: '10px',
          },
        },

        '.MuiStepLabel-root': {
          padding: theme.spacing(1, 2),
          overflow: 'hidden',
          border: `2px solid transparent`,
          borderRadius: Number(theme.shape.borderRadius) * 3,
          backgroundColor: `color-mix(in srgb, ${theme.vars.palette.grey.A100} 30%, transparent)`,

          ' svg': {
            width: '16px',
            height: '16px',
            fill: theme.vars.palette.grey[400],
          },

          '.MuiStepLabel-label': {
            color: theme.vars.palette.grey[400],
            ...theme.typography.body2,
            fontWeight: 700,
          },

          '&.Mui-active, &.Mui-completed': {
            ' svg': {
              fill: theme.vars.palette.primary.main,
              ...theme.applyStyles('dark', { fill: theme.vars.palette.primary.light }),
            },
            borderColor: theme.vars.palette.primary.main,
            '.MuiStepLabel-label': {
              color: theme.vars.palette.primary.main,
              ...theme.applyStyles('dark', { color: theme.vars.palette.primary.light }),
            },
            ...theme.applyStyles('dark', { borderColor: theme.vars.palette.primary.light }),
          },

          '&.Mui-completed': {
            backgroundColor: `color-mix(in srgb, ${theme.vars.palette.grey.A100} 30%, transparent)`,
            border: `2px solid transparent`,
          },

          '&.Mui-active': {
            background: theme.vars.palette.background.paper,
          },
        },
      }),
    },
  },
};
